/**
 * Event → allowed blocking actions (shared UI + validation contract).
 * Backend SaveTrigger must enforce the same rules.
 */
export const EPSF_FORM_EVENTS = [
  {
    value: "OnReceipt",
    eventCode: "RECEIPT_POSTED",
    label: "Receipt posted",
    allowedBlockers: [
      {
        value: "RELEASE_TO_AVAILABLE",
        label: "Blocks release to available",
      },
    ],
    titleTokens: ["{Study}", "{Site}", "{Date}", "{ReceiptNo}", "{TransactionNo}"],
  },
  {
    value: "OnDispense",
    eventCode: "DISPENSE_POSTED",
    label: "Dispense posted",
    allowedBlockers: [
      {
        value: "FINAL_DISPENSE",
        label: "Blocks final dispense",
      },
    ],
    titleTokens: ["{Study}", "{Site}", "{Date}", "{TransactionNo}"],
  },
  {
    value: "DESTRUCTION_REQUESTED",
    eventCode: "DESTRUCTION_REQUESTED",
    label: "Destruction requested",
    allowedBlockers: [
      {
        value: "CONFIRM_DESTRUCTION",
        label: "Blocks confirm destruction",
      },
    ],
    titleTokens: ["{Study}", "{Site}", "{Date}", "{TransactionNo}"],
  },
  {
    value: "RECONCILIATION_STARTED",
    eventCode: "RECONCILIATION_STARTED",
    label: "Reconciliation started",
    allowedBlockers: [
      {
        value: "COMPLETE_RECONCILIATION",
        label: "Blocks complete reconciliation",
      },
    ],
    titleTokens: ["{Study}", "{Site}", "{Date}"],
  },
  /* Deferred: Return requested (OnParticipantReturn / PARTICIPANT_RETURN_REQUESTED) — re-enable later. */
];

export const DUE_UNIT_OPTIONS = [
  { value: "WITHIN_MINUTES", label: "Minutes" },
  { value: "WITHIN_HOURS", label: "Hours" },
  { value: "WITHIN_DAYS", label: "Days" },
];

export const OVERDUE_UNIT_OPTIONS = [
  { value: "MINUTES", label: "Minutes" },
  { value: "HOURS", label: "Hours" },
  { value: "DAYS", label: "Days" },
];

export function getEventMeta(triggerCode) {
  const code = String(triggerCode || "").trim();
  return (
    EPSF_FORM_EVENTS.find((e) => e.value === code || e.eventCode === code) ||
    null
  );
}

export function eventCodeForTrigger(triggerCode) {
  const meta = getEventMeta(triggerCode);
  return meta?.eventCode || String(triggerCode || "").trim();
}

export function blockingOptionsForEvent(triggerCode, { includeNone = true } = {}) {
  const meta = getEventMeta(triggerCode);
  const blockers = (meta?.allowedBlockers || []).map((b) => ({
    value: b.value,
    label: b.label,
  }));
  if (includeNone) {
    return [{ value: "", label: "None" }, ...blockers];
  }
  return blockers;
}

export function isBlockingActionAllowed(triggerCode, blockingActionCode) {
  const code = String(blockingActionCode || "").trim();
  if (!code) return true;
  const meta = getEventMeta(triggerCode);
  if (!meta) return false;
  return (meta.allowedBlockers || []).some((b) => b.value === code);
}

export function titleTokensForEvent(triggerCode) {
  return getEventMeta(triggerCode)?.titleTokens || ["{Study}", "{Site}", "{Date}"];
}

/** Safe preview only — never expands restricted product/treatment tokens. */
export function previewGeneratedTitle(template, ctx = {}) {
  const raw = String(template || "").trim();
  if (!raw) return "";
  const study = String(ctx.studyLabel || ctx.studyId || "Study").slice(0, 80);
  const site = String(ctx.siteLabel || ctx.siteId || "Site").slice(0, 80);
  const date = ctx.dateLabel || new Date().toLocaleDateString();
  const receipt = ctx.receiptNo || "REC-XXXX";
  const txn = ctx.transactionNo || "TXN-XXXX";
  return raw
    .replace(/\{Study\}/gi, study)
    .replace(/\{Site\}/gi, site)
    .replace(/\{Date\}/gi, date)
    .replace(/\{ReceiptNo\}/gi, receipt)
    .replace(/\{TransactionNo\}/gi, txn)
    .replace(/\{Period\}/gi, ctx.period || "Period");
}

export function overdueToMinutes(value, unit) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  const u = String(unit || "MINUTES").toUpperCase();
  if (u === "DAYS") return Math.round(n * 24 * 60);
  if (u === "HOURS") return Math.round(n * 60);
  return Math.round(n);
}

export function minutesToOverdueParts(minutes) {
  const m = Number(minutes);
  if (!Number.isFinite(m) || m <= 0) {
    return { value: "60", unit: "MINUTES" };
  }
  if (m % (24 * 60) === 0) {
    return { value: String(m / (24 * 60)), unit: "DAYS" };
  }
  if (m % 60 === 0) {
    return { value: String(m / 60), unit: "HOURS" };
  }
  return { value: String(m), unit: "MINUTES" };
}
